import { SubstepType } from "~/features/configurator/Linear/dtos";
import {
  makeLabelGetter,
  makeNumberGetter,
  prepareSubStepsInfo,
  type SummaryData,
  type SummaryDataProvider,
} from "~/features/configurator/Shaft/SummaryDataProvider";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import { InputCstics } from "~/features/configurator/Rail/components/ConfigureRailSubstep/constants";
import { removeMultipleTags } from "~/common/utils/domUtils";
import { getMainKmatCstics } from "~/features/configurator/common/v2/helper";
import type { LinearCstic } from "~/features/configurator/Linear/models";

export function getStep1SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps: SubstepType[] = [];

  return prepareSubStepsInfo(summaryProvider, substeps);
}

export function getStep2SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps = [SubstepType.Coating, SubstepType.Accuracy];

  return prepareSubStepsInfo(summaryProvider, substeps);
}

export function getStep3SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps = [SubstepType.Mounting, SubstepType.Closure];

  return prepareSubStepsInfo(summaryProvider, substeps);
}

export function getStep4SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps = [SubstepType.LocatingFace];

  const summaryData = prepareSubStepsInfo(summaryProvider, substeps) ?? [];

  const t = summaryProvider.t;
  const configureRailSubstep = summaryProvider.substeps[SubstepType.ConfigureRail];
  if (configureRailSubstep) {
    const inputCsticNames = [
      InputCstics.TotalLength,
      InputCstics.aL,
      InputCstics.aR,
      InputCstics.partSizingType,
      InputCstics.partAmount,
      InputCstics.partOne,
      InputCstics.partTwo,
      InputCstics.partThree,
      InputCstics.partFour,
      InputCstics.partFive,
    ];

    const inputCsticNamesFormatting: Record<string, (cstic: LinearCstic) => string> = {
      [InputCstics.TotalLength]: makeNumberGetter(),
      [InputCstics.aL]: makeNumberGetter(),
      [InputCstics.aR]: makeNumberGetter(),
      [InputCstics.partSizingType]: makeLabelGetter(t),
      [InputCstics.partAmount]: makeLabelGetter(t),
      [InputCstics.partOne]: makeNumberGetter(),
      [InputCstics.partTwo]: makeNumberGetter(),
      [InputCstics.partThree]: makeNumberGetter(),
      [InputCstics.partFour]: makeNumberGetter(),
      [InputCstics.partFive]: makeNumberGetter(),
    };

    const inputCstics = getMainKmatCstics(configureRailSubstep).input;
    const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;

    inputCsticNames.forEach((inputCsticName) => {
      const def = inputCstics[inputCsticName];
      const cstic = cstics[def.name];
      const getValue = inputCsticNamesFormatting[inputCsticName];

      if (!cstic || !def.visible) {
        return;
      }

      summaryData.push({
        name: t(cstic.label),
        isStandard: false,
        value: getValue(cstic),
      });
    });
  }

  summaryData.forEach((data) => {
    data.value = removeMultipleTags(data.value, "p", "span")!;
    data.name = removeMultipleTags(data.name, "p", "span")!;
  });

  return summaryData;
}
